import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Project, Task, ViewType, WeekViewMode } from '../types';

// 认证状态
interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setUser: (user: User | null) => void;
  setAuthenticated: (value: boolean) => void;
  setLoading: (value: boolean) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  setUser: (user) => set({ user, isAuthenticated: !!user }),
  setAuthenticated: (value) => set({ isAuthenticated: value }),
  setLoading: (value) => set({ isLoading: value }),
  logout: () => {
    localStorage.removeItem('token');
    set({ user: null, isAuthenticated: false });
  },
}));

// UI状态
interface UIState {
  currentView: ViewType;
  weekViewMode: WeekViewMode;
  sidebarOpen: boolean;
  selectedProjectId: string | null;
  selectedTaskId: string | null;
  isTaskModalOpen: boolean;
  isProjectModalOpen: boolean;
  setCurrentView: (view: ViewType) => void;
  setWeekViewMode: (mode: WeekViewMode) => void;
  toggleSidebar: () => void;
  setSidebarOpen: (value: boolean) => void;
  setSelectedProjectId: (id: string | null) => void;
  setSelectedTaskId: (id: string | null) => void;
  openTaskModal: (taskId?: string) => void;
  closeTaskModal: () => void;
  openProjectModal: (projectId?: string) => void;
  closeProjectModal: () => void;
}

export const useUIStore = create<UIState>((set) => ({
  currentView: 'list',
  weekViewMode: 'timeline',
  sidebarOpen: true,
  selectedProjectId: null,
  selectedTaskId: null,
  isTaskModalOpen: false,
  isProjectModalOpen: false,
  setCurrentView: (view) => set({ currentView: view }),
  setWeekViewMode: (mode) => set({ weekViewMode: mode }),
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  setSidebarOpen: (value) => set({ sidebarOpen: value }),
  setSelectedProjectId: (id) => set({ selectedProjectId: id }),
  setSelectedTaskId: (id) => set({ selectedTaskId: id }),
  openTaskModal: (taskId) => set({ isTaskModalOpen: true, selectedTaskId: taskId || null }),
  closeTaskModal: () => set({ isTaskModalOpen: false, selectedTaskId: null }),
  openProjectModal: (projectId) => set({ isProjectModalOpen: true, selectedProjectId: projectId || null }),
  closeProjectModal: () => set({ isProjectModalOpen: false, selectedProjectId: null }),
}));

// 数据状态
interface DataState {
  projects: Project[];
  tasks: Task[];
  isLoading: boolean;
  setProjects: (projects: Project[]) => void;
  setTasks: (tasks: Task[]) => void;
  addProject: (project: Project) => void;
  updateProject: (project: Project) => void;
  removeProject: (id: string) => void;
  addTask: (task: Task) => void;
  updateTask: (task: Task) => void;
  removeTask: (id: string) => void;
  setLoading: (value: boolean) => void;
}

export const useDataStore = create<DataState>((set) => ({
  projects: [],
  tasks: [],
  isLoading: false,
  setProjects: (projects) => set({ projects }),
  setTasks: (tasks) => set({ tasks }),
  addProject: (project) => set((state) => ({ 
    projects: [...state.projects, project] 
  })),
  updateProject: (project) => set((state) => ({
    projects: state.projects.map((p) => (p.id === project.id ? project : p)),
  })),
  removeProject: (id) => set((state) => ({
    projects: state.projects.filter((p) => p.id !== id),
  })),
  addTask: (task) => set((state) => ({ 
    tasks: [...state.tasks, task] 
  })),
  updateTask: (task) => set((state) => ({
    tasks: state.tasks.map((t) => (t.id === task.id ? task : t)),
  })),
  removeTask: (id) => set((state) => ({
    tasks: state.tasks.filter((t) => t.id !== id),
  })),
  setLoading: (value) => set({ isLoading: value }),
}));

// MindMap状态
interface MindMapState {
  scale: number;
  translateX: number;
  translateY: number;
  isDragging: boolean;
  draggedNodeId: string | null;
  setScale: (scale: number) => void;
  setTranslate: (x: number, y: number) => void;
  setIsDragging: (value: boolean) => void;
  setDraggedNodeId: (id: string | null) => void;
  resetView: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
}

export const useMindMapStore = create<MindMapState>((set) => ({
  scale: 1,
  translateX: 0,
  translateY: 0,
  isDragging: false,
  draggedNodeId: null,
  setScale: (scale) => set({ scale: Math.max(0.1, Math.min(3, scale)) }),
  setTranslate: (x, y) => set({ translateX: x, translateY: y }),
  setIsDragging: (value) => set({ isDragging: value }),
  setDraggedNodeId: (id) => set({ draggedNodeId: id }),
  resetView: () => set({ scale: 1, translateX: 0, translateY: 0 }),
  zoomIn: () => set((state) => ({ scale: Math.min(3, state.scale * 1.2) })),
  zoomOut: () => set((state) => ({ scale: Math.max(0.1, state.scale / 1.2) })),
}));

// 日历状态
interface CalendarState {
  currentDate: Date;
  selectedDate: Date | null;
  setCurrentDate: (date: Date) => void;
  setSelectedDate: (date: Date | null) => void;
  goToToday: () => void;
  goToPrevious: () => void;
  goToNext: () => void;
}

export const useCalendarStore = create<CalendarState>((set) => ({
  currentDate: new Date(),
  selectedDate: null,
  setCurrentDate: (date) => set({ currentDate: date }),
  setSelectedDate: (date) => set({ selectedDate: date }),
  goToToday: () => set({ currentDate: new Date() }),
  goToPrevious: () => set((state) => {
    const newDate = new Date(state.currentDate);
    newDate.setDate(newDate.getDate() - 7);
    return { currentDate: newDate };
  }),
  goToNext: () => set((state) => {
    const newDate = new Date(state.currentDate);
    newDate.setDate(newDate.getDate() + 7);
    return { currentDate: newDate };
  }),
}));
